/**
 * One structured attribute as ONE row of inputs.
 *
 * A centre of gravity is three numbers, an inertia tensor is six about a point
 * that is three more, and a temperature band is two ends. The registry declares
 * a field per LEAF - a value is stored at the feature path the model wrote,
 * which is what lets the library add a structure without a migration - so a form
 * built one control per field asked for a vehicle's mass properties across
 * fifteen separate rows, each with its own floating label repeating the same
 * three words.
 *
 * So the leaves come back together: one heading, one unit, and the components
 * side by side in the order the model declares them. For the tensor that order
 * is already the upper triangle - Ixx, Iyy, Izz, Ixy, Ixz, Iyz - which is how a
 * symmetric tensor is quoted, so nothing here has to know it is one.
 *
 * WHAT THIS DOES NOT DO is parse or compose a quantity. Each cell is a
 * `FieldControl`, which sends a NUMBER and lets the server compose the unit from
 * the field's own declaration. A row of plain text boxes here would be a second
 * place that writes a quantity, and the first one is what put
 * `:>> dryMass = "3.5 [SI::kg]";` into the model.
 */

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import FieldControl from './FieldControl'
import HelpTip from '../common/HelpTip'
import { subLabelOf } from '../../schema'
import type { FieldGroup, FieldSchema, KindSchema, Schema } from '../../schema'
import { unitLabel } from '../budgets/budget-model'

/** How wide one component is. Six of these have to fit a form column. */
const CELL = '7.5rem'

export default function QuantityGroup({
  schema,
  kind,
  group,
  valueOf,
  onChange,
  disabled,
  fieldErrors = {},
}: {
  schema: Schema
  kind: KindSchema
  group: FieldGroup
  valueOf: (field: FieldSchema) => unknown
  onChange: (field: FieldSchema, next: unknown) => void
  disabled?: boolean
  fieldErrors?: Record<string, string>
}) {
  const first = group.fields[0]
  // The unit is a property of the FIELD, and every leaf of one structure
  // declares the same one - so it is said once, after the row, rather than six
  // times inside it. Where they somehow differ, each cell keeps its own: that
  // is a model problem and hiding it would be this screen inventing agreement.
  const units = new Set(group.fields.map((field) => field.unit))
  const shared = units.size === 1 ? unitLabel(first.unit) : ''

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {group.label}
        </Typography>
        {first.help && (
          <HelpTip title={first.help} label={`What ${group.label} means`} />
        )}
        {shared && (
          <Typography variant="caption" color="text.disabled" sx={{ ml: 1 }}>
            {shared}
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
        {group.fields.map((field) => {
          const sub = subLabelOf(field, group.label)
          return (
            <Box key={field.name} sx={{ width: CELL }}>
              <FieldControl
                schema={schema}
                kind={kind}
                // The sub-label is the cell's own caption below; the control
                // carries the FULL name only for a screen reader, because 'x'
                // on its own identifies nothing once focus is inside the row.
                // The unit is KEPT on the field and only hidden: it decides
                // what gets written, not just what is drawn.
                field={{ ...field, label: '', help: '' }}
                hideUnit={Boolean(shared)}
                ariaLabel={`${group.label} ${sub}`.trim()}
                value={valueOf(field)}
                onChange={(next) => onChange(field, next)}
                disabled={disabled}
                error={fieldErrors[field.name]}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', textAlign: 'center' }}
              >
                {sub}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}
